import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ExternalLink, AlertTriangle, CheckCircle, AlertCircle, Minus, Loader2, Download, Building2, Beaker, DollarSign, TrendingUp, Clock, Sparkles, RotateCcw, FlaskConical } from "lucide-react";
import { useCompare } from "@/context/CompareContext";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";

const STATUS_CONFIG = {
  PASS: { label: "Risk Detected", color: "bg-red-100 text-red-700 border-red-200", icon: AlertTriangle },
  CAUTION: { label: "Mixed Signals", color: "bg-amber-100 text-amber-700 border-amber-200", icon: AlertCircle },
  INSPECT: { label: "Clean Profile", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
  INSUFFICIENT: { label: "No Data", color: "bg-slate-100 text-slate-500 border-slate-200", icon: Minus },
};

const STATUS_COLORS = {
  PASS: [220, 38, 38],
  CAUTION: [245, 158, 11],
  INSPECT: [5, 150, 105],
  INSUFFICIENT: [148, 163, 184],
};

// Normalize backend status vocabulary ("CLEAN PROFILE", "RISK DETECTED",
// "MIXED SIGNALS", "INSUFFICIENT"/"INSUFFICIENT DATA") to the modal's config codes.
const normalizeStatus = (raw) => {
  const v = (raw || "").toString().trim().toUpperCase();
  if (v === "PASS" || v === "RISK DETECTED" || v === "RISK") return "PASS";
  if (v === "CAUTION" || v === "MIXED SIGNALS" || v === "MIXED") return "CAUTION";
  if (v === "INSPECT" || v === "CLEAN PROFILE" || v === "CLEAN") return "INSPECT";
  return "INSUFFICIENT";
};

// Helper to determine cell highlighting
const getCellHighlight = (items, key, comparator, highlightBest = true) => {
  const values = items.map((item) => ({ query: item.original || item.query, value: comparator(item) }));
  
  if (highlightBest) {
    const best = Math.max(...values.map((v) => v.value));
    const worst = Math.min(...values.map((v) => v.value));
    return values.reduce((acc, v) => {
      if (v.value === best && best > 0) acc[v.query] = "best";
      if (v.value === worst && worst < 0) acc[v.query] = "worst";
      return acc;
    }, {});
  }
  return {};
};

// Extract phase number from phase string
const extractPhaseNumber = (phaseStr) => {
  if (!phaseStr) return 0;
  const match = phaseStr.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

export default function CompareModal() {
  const { compareItems, compareData, isModalOpen, closeCompareModal, isLoading, revertToOriginal } = useCompare();
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();

  // Deep-link a compared row to its full dossier (trials, patents, financials)
  const goToDossier = (it) => {
    const q = encodeURIComponent(it.original || it.query);
    const type = it.isCompany ? "company" : "asset";
    closeCompareModal();
    navigate(`/research?q=${q}&type=${type}`);
  };

  if (!isModalOpen) return null;

  // Generate comparison PDF
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      // Create landscape PDF
      const doc = new jsPDF({ orientation: "landscape" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = 15;

      // Header - Phase 93: Updated branding
      doc.setFillColor(15, 23, 42);
      doc.rect(margin, yPos, 12, 12, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.text("BDS", margin + 2, yPos + 8);

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(14);
      doc.text("BioDataStudio", margin + 16, yPos + 8);

      // Date and title
      const currentDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text(currentDate, pageWidth - margin, yPos + 4, { align: "right" });

      // Confidential badge
      doc.setFillColor(254, 242, 242);
      doc.setDrawColor(254, 202, 202);
      doc.roundedRect(pageWidth - margin - 40, yPos + 6, 40, 8, 1, 1, "FD");
      doc.setFontSize(7);
      doc.setTextColor(185, 28, 28);
      doc.setFont("helvetica", "bold");
      doc.text("CONFIDENTIAL", pageWidth - margin - 20, yPos + 11.5, { align: "center" });

      yPos += 25;

      // Divider
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      // Title
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text("QUICK COMPARE ANALYSIS", margin, yPos);
      yPos += 8;

      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.text(items.map((i) => i.query.toUpperCase()).join(" vs "), margin, yPos);
      yPos += 12;

      // Build table data
      const tableHeaders = ["Metric", ...items.map((i) => i.query.toUpperCase())];
      const tableBody = [
        ["Status", ...items.map((i) => STATUS_CONFIG[i.status]?.label || "Unknown")],
        ["Clinical Phase", ...items.map((i) => i.highestPhase)],
        ["Safety Profile", ...items.map((i) => i.safetyProfile)],
        ["Patent Expiry", ...items.map((i) => i.patentExpiry)],
        ["Lead Sponsor", ...items.map((i) => i.sponsor)],
      ];

      // Generate table
      autoTable(doc, {
        startY: yPos,
        head: [tableHeaders],
        body: tableBody,
        theme: "grid",
        styles: {
          fontSize: 9,
          cellPadding: 4,
          font: "helvetica",
          lineColor: [226, 232, 240],
          lineWidth: 0.5,
        },
        headStyles: {
          fillColor: [241, 245, 249],
          textColor: [15, 23, 42],
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 35, halign: "left", fillColor: [248, 250, 252] },
        },
        bodyStyles: {
          halign: "center",
        },
        didParseCell: function (data) {
          // Highlight status cells
          if (data.row.index === 0 && data.column.index > 0) {
            const status = items[data.column.index - 1]?.status;
            if (status === "PASS") {
              data.cell.styles.fillColor = [254, 242, 242];
              data.cell.styles.textColor = [185, 28, 28];
            } else if (status === "CAUTION") {
              data.cell.styles.fillColor = [255, 251, 235];
              data.cell.styles.textColor = [180, 83, 9];
            } else if (status === "INSPECT") {
              data.cell.styles.fillColor = [236, 253, 245];
              data.cell.styles.textColor = [6, 95, 70];
            }
          }
          // Highlight best phase
          if (data.row.index === 1 && data.column.index > 0) {
            const item = items[data.column.index - 1];
            if (phaseHighlights[item?.query] === "best") {
              data.cell.styles.fillColor = [236, 253, 245];
              data.cell.styles.textColor = [6, 95, 70];
            }
          }
          // Highlight safety
          if (data.row.index === 2 && data.column.index > 0) {
            const item = items[data.column.index - 1];
            if (safetyHighlights[item?.query] === "best") {
              data.cell.styles.fillColor = [236, 253, 245];
              data.cell.styles.textColor = [6, 95, 70];
            } else if (safetyHighlights[item?.query] === "worst") {
              data.cell.styles.fillColor = [254, 242, 242];
              data.cell.styles.textColor = [185, 28, 28];
            }
          }
        },
        margin: { left: margin, right: margin },
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Legend
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text("Legend:", margin, yPos);

      doc.setFillColor(236, 253, 245);
      doc.rect(margin + 20, yPos - 4, 8, 6, "F");
      doc.text("Best in Category", margin + 30, yPos);

      doc.setFillColor(254, 242, 242);
      doc.rect(margin + 80, yPos - 4, 8, 6, "F");
      doc.text("Critical / Worst", margin + 90, yPos);

      // Footer
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `Generated by BioDataStudio | ${currentDate}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
      doc.text(
        "This document is confidential and intended for internal use only.",
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 6,
        { align: "center" }
      );

      // Download using anchor tag method
      const pdfBlob = doc.output("blob");
      const blobUrl = URL.createObjectURL(pdfBlob);
      const fileName = `BioDataStudio_Compare_${items.map((i) => i.query).join("_vs_")}_${new Date().toISOString().split("T")[0]}.pdf`;

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);

      toast.success("Comparison PDF exported successfully");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    } finally {
      setIsExporting(false);
    }
  };

  // Prepare data for comparison
  const items = compareItems.map((item) => {
    // Phase 93: Use original name as cache key (Smart Alias stores data by original name)
    const cacheKey = (item.original || item.query).toLowerCase();
    const data = compareData[cacheKey] || {};
    const clinical = data.clinical || {};
    const patents = data.patents || {};
    const safety = data.safety || {};
    const companyProfile = data.company_profile || {};
    const pipeline = data.pipeline || {};

    // Detect if this is a company comparison
    const isCompany = data.query_type === "company" || data.entity_type === "company" || item.entityType === "company";
    const normStatus = normalizeStatus(data.status);

    // Extract highest phase - handle both "Phase X" and "X" key formats
    const phases = clinical.phases || pipeline.phase_distribution || {};
    let highestPhase = "N/A";
    if ((phases["Phase 4"] || phases["4"]) > 0) highestPhase = "Phase 4 (Market)";
    else if ((phases["Phase 3"] || phases["3"]) > 0) highestPhase = "Phase 3";
    else if ((phases["Phase 2"] || phases["2"]) > 0) highestPhase = "Phase 2";
    else if ((phases["Phase 1"] || phases["1"]) > 0) highestPhase = "Phase 1";
    else if (clinical.total_trials > 0) highestPhase = "Pre-Clinical";

    // Safety profile
    const safetySignals = safety.signals || [];
    const safetyProfile = safetySignals.length === 0 ? "Clean" : safetySignals.slice(0, 3).join(", ");
    const hasCriticalSafety = normStatus === "PASS" && safety.total_reports > 0;

    // Patent expiry (estimate earliest filing + 20). Supports both asset-shaped
    // (earliest_filing / top_assignees) and company-shaped (patent_timeline) responses.
    let patentExpiry = "N/A";
    let earliestFilingYear = null;
    if (patents.earliest_filing) {
      earliestFilingYear = parseInt(String(patents.earliest_filing).split("-")[0], 10);
    } else if (Array.isArray(patents.top_assignees)) {
      const yrs = patents.top_assignees
        .flatMap((a) => (a.patents || []).map((p) => parseInt(p.filing_year || (p.priority_date || "").split("-")[0], 10)))
        .filter(Boolean);
      if (yrs.length) earliestFilingYear = Math.min(...yrs);
    }
    if (!earliestFilingYear && Array.isArray(patents.patent_timeline)) {
      const yrs = patents.patent_timeline
        .map((t) => parseInt(t.year || t.filing_year || t.date, 10))
        .filter(Boolean);
      if (yrs.length) earliestFilingYear = Math.min(...yrs);
    }
    if (earliestFilingYear) patentExpiry = `~${earliestFilingYear + 20}`;

    const patentTotal = patents.total_patents ?? patents.total ?? 0;

    // Lead sponsor: assets carry clinical.lead_sponsor; a company is effectively its own sponsor.
    const topSponsor = Array.isArray(clinical.top_sponsors) ? clinical.top_sponsors[0] : null;
    const sponsor = clinical.lead_sponsor
      || (topSponsor && (topSponsor.name || topSponsor))
      || patents.top_assignees?.[0]?.name
      || companyProfile.name || companyProfile.company_name
      || data.resolved_entity || patents.company_name
      || (isCompany ? item.query : "Unknown");

    // Company-specific metrics
    const totalTrials = clinical.total_trials || pipeline.total_trials || 0;
    const activeTrials = clinical.active_trials || pipeline.active_trials || 0;
    const pipelineSize = pipeline.total_assets || pipeline.top_assets?.length || 0;
    // Phase counts - handle both "Phase 3" and "3" key formats from API
    const phase3Count = phases["Phase 3"] || phases["3"] || pipeline.phase_distribution?.["Phase 3"] || pipeline.phase_distribution?.["3"] || 0;
    const phase2Count = phases["Phase 2"] || phases["2"] || pipeline.phase_distribution?.["Phase 2"] || pipeline.phase_distribution?.["2"] || 0;
    const phase1Count = phases["Phase 1"] || phases["1"] || pipeline.phase_distribution?.["Phase 1"] || pipeline.phase_distribution?.["1"] || 0;
    
    // Financial metrics from company_profile
    const cashPosition = companyProfile.cash_position || companyProfile.financials?.cash_position || 0;
    const burnRate = companyProfile.quarterly_burn || companyProfile.financials?.quarterly_burn || 0;
    const runwayMonths = burnRate > 0 ? Math.round(cashPosition / burnRate) : 0;
    const marketCap = companyProfile.market_cap || 0;
    
    // Therapeutic areas
    const therapeuticAreas = pipeline.therapeutic_areas || companyProfile.therapeutic_areas || [];
    
    return {
      query: item.query,
      // Phase 93: Smart Alias fields
      original: item.original || item.query,
      search: item.search || item.query,
      isSimplified: item.isSimplified || false,
      isCompany,
      status: normStatus,
      highestPhase,
      phaseNumber: extractPhaseNumber(highestPhase),
      safetyProfile,
      safetyCount: safetySignals.length,
      hasCriticalSafety,
      patentExpiry,
      sponsor,
      isTerminated: clinical.terminated_trials > 0,
      terminatedCount: clinical.terminated_trials || 0,
      // Company metrics
      totalTrials,
      activeTrials,
      pipelineSize,
      phase3Count,
      phase2Count,
      phase1Count,
      cashPosition,
      burnRate,
      runwayMonths,
      marketCap,
      therapeuticAreas,
      patentCount: patentTotal,
    };
  });

  // Detect if this is a company comparison
  const isCompanyComparison = items.some(item => item.isCompany);

  // Determine highlights
  const phaseHighlights = {};
  const safetyHighlights = {};
  
  if (items.length >= 2) {
    // Best phase (highest number wins)
    const maxPhase = Math.max(...items.map((i) => i.phaseNumber));
    items.forEach((item) => {
      if (item.phaseNumber === maxPhase && maxPhase > 0) {
        phaseHighlights[item.query] = "best";
      }
    });

    // Cleanest safety (lowest count wins)
    const minSafety = Math.min(...items.map((i) => i.safetyCount));
    items.forEach((item) => {
      if (item.safetyCount === minSafety) {
        safetyHighlights[item.query] = "best";
      }
      if (item.hasCriticalSafety) {
        safetyHighlights[item.query] = "worst";
      }
    });
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="compare-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={closeCompareModal}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-sm shadow-sm max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {isCompanyComparison ? "Pipeline Comparison" : "Quick Compare"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {isCompanyComparison 
                ? `M&A Due Diligence: ${items.length} companies` 
                : `Side-by-side analysis of ${items.length} assets`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Export PDF Button */}
            <button
              onClick={handleExportPDF}
              disabled={isExporting || isLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-sm text-xs font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="export-compare-pdf-btn"
            >
              {isExporting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={14} />
                  Share / Export
                </>
              )}
            </button>
            {/* Close Button */}
            <button
              onClick={closeCompareModal}
              className="p-2 hover:bg-slate-100 rounded-sm transition-colors"
              data-testid="close-compare-modal-btn"
            >
              <X size={18} className="text-slate-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-auto max-h-[calc(90vh-80px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Fetching comparison data...</span>
            </div>
          ) : isCompanyComparison ? (
            /* Company Pipeline Comparison View */
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left py-3 px-4 bg-slate-50 border border-slate-200 font-mono text-[10px] text-slate-500 uppercase tracking-widest w-40">
                      M&A Due Diligence
                    </th>
                    {items.map((item) => (
                      <th
                        key={item.query}
                        className="text-center py-3 px-4 bg-slate-50 border border-slate-200 min-w-[180px]"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Building2 size={14} className="text-slate-400" />
                          <span className="font-mono text-sm font-semibold text-slate-900">
                            {item.query.toUpperCase()}
                          </span>
                        </div>
                        {/* Phase 93: Smart Alias indicator for companies */}
                        {item.isSimplified && (
                          <div className="mt-1.5 space-y-1">
                            <div className="flex items-center justify-center gap-1">
                              <Sparkles size={10} className="text-emerald-500" />
                              <span className="text-[9px] text-emerald-600 font-medium">Smart Search</span>
                            </div>
                            <div className="text-[9px] text-slate-500 max-w-[160px] truncate mx-auto" title={item.original}>
                              Full: {item.original}
                            </div>
                            <button
                              onClick={() => revertToOriginal(item.original)}
                              className="inline-flex items-center gap-1 text-[9px] text-slate-500 hover:text-slate-700 hover:underline"
                              data-testid={`revert-alias-company-${item.query.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <RotateCcw size={8} />
                              Use full name
                            </button>
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Full dossier deep-link */}
                  <tr>
                    <td className="py-2 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Full Dossier</span>
                    </td>
                    {items.map((item) => (
                      <td key={item.query} className="py-2 px-4 border border-slate-200 text-center">
                        <button
                          onClick={() => goToDossier(item)}
                          data-testid={`compare-dossier-${item.query.toLowerCase().replace(/\s+/g, '-')}`}
                          className="text-[11px] text-[#2A5D9E] hover:underline inline-flex items-center gap-1"
                        >
                          Open dossier <ExternalLink size={11} />
                        </button>
                      </td>
                    ))}
                  </tr>
                  {/* Pipeline Size */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <div className="flex items-center gap-2">
                        <Beaker size={12} className="text-slate-400" />
                        <span className="label-dense">Pipeline Size</span>
                      </div>
                    </td>
                    {items.map((item) => {
                      const isBest = item.pipelineSize === Math.max(...items.map(i => i.pipelineSize)) && item.pipelineSize > 0;
                      return (
                        <td key={item.query} className={`py-3 px-4 border border-slate-200 text-center ${isBest ? "bg-emerald-50" : ""}`}>
                          <span className={`font-mono text-lg font-bold ${isBest ? "text-emerald-700" : "text-slate-900"}`}>
                            {item.pipelineSize || "—"}
                          </span>
                          <div className="text-[10px] text-slate-500">assets</div>
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Active Trials */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <div className="flex items-center gap-2">
                        <TrendingUp size={12} className="text-slate-400" />
                        <span className="label-dense">Active Trials</span>
                      </div>
                    </td>
                    {items.map((item) => {
                      const isBest = item.activeTrials === Math.max(...items.map(i => i.activeTrials)) && item.activeTrials > 0;
                      return (
                        <td key={item.query} className={`py-3 px-4 border border-slate-200 text-center ${isBest ? "bg-emerald-50" : ""}`}>
                          <span className={`font-mono text-lg font-bold ${isBest ? "text-emerald-700" : "text-slate-900"}`}>
                            {item.activeTrials || "—"}
                          </span>
                          <div className="text-[10px] text-slate-500">of {item.totalTrials} total</div>
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Phase Distribution */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Phase Distribution</span>
                    </td>
                    {items.map((item) => {
                      const hasPhase3 = item.phase3Count > 0;
                      return (
                        <td key={item.query} className={`py-3 px-4 border border-slate-200 text-center ${hasPhase3 ? "bg-blue-50" : ""}`}>
                          <div className="flex items-center justify-center gap-2 text-xs">
                            <span className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded">P1: {item.phase1Count}</span>
                            <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded">P2: {item.phase2Count}</span>
                            <span className={`px-2 py-0.5 rounded ${hasPhase3 ? "bg-emerald-200 text-emerald-800 font-semibold" : "bg-slate-200 text-slate-700"}`}>
                              P3: {item.phase3Count}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Cash Runway */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <div className="flex items-center gap-2">
                        <Clock size={12} className="text-slate-400" />
                        <span className="label-dense">Cash Runway</span>
                      </div>
                    </td>
                    {items.map((item) => {
                      const isCritical = item.runwayMonths > 0 && item.runwayMonths < 12;
                      const isHealthy = item.runwayMonths >= 24;
                      return (
                        <td key={item.query} className={`py-3 px-4 border border-slate-200 text-center ${isCritical ? "bg-red-50" : isHealthy ? "bg-emerald-50" : ""}`}>
                          {item.runwayMonths > 0 ? (
                            <>
                              <span className={`font-mono text-lg font-bold ${isCritical ? "text-red-600" : isHealthy ? "text-emerald-700" : "text-slate-900"}`}>
                                {item.runwayMonths}
                              </span>
                              <span className="text-sm text-slate-500"> mo</span>
                              {isCritical && <div className="text-[10px] text-red-600 mt-0.5">⚠️ Critical</div>}
                              {isHealthy && <div className="text-[10px] text-emerald-600 mt-0.5">Healthy</div>}
                            </>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Cash Position */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <div className="flex items-center gap-2">
                        <DollarSign size={12} className="text-slate-400" />
                        <span className="label-dense">Cash Position</span>
                      </div>
                    </td>
                    {items.map((item) => {
                      const isBest = item.cashPosition === Math.max(...items.map(i => i.cashPosition)) && item.cashPosition > 0;
                      return (
                        <td key={item.query} className={`py-3 px-4 border border-slate-200 text-center ${isBest ? "bg-emerald-50" : ""}`}>
                          {item.cashPosition > 0 ? (
                            <span className={`font-mono text-sm font-semibold ${isBest ? "text-emerald-700" : "text-slate-900"}`}>
                              ${(item.cashPosition / 1000000).toFixed(0)}M
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Patent Portfolio */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Patent Portfolio</span>
                    </td>
                    {items.map((item) => {
                      const isBest = item.patentCount === Math.max(...items.map(i => i.patentCount)) && item.patentCount > 0;
                      return (
                        <td key={item.query} className={`py-3 px-4 border border-slate-200 text-center ${isBest ? "bg-blue-50" : ""}`}>
                          <span className={`font-mono text-sm ${isBest ? "text-blue-700 font-semibold" : "text-slate-700"}`}>
                            {item.patentCount || "—"} patents
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Therapeutic Areas */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Therapeutic Focus</span>
                    </td>
                    {items.map((item) => (
                      <td key={item.query} className="py-3 px-4 border border-slate-200 text-center">
                        <div className="flex flex-wrap justify-center gap-1">
                          {item.therapeuticAreas.length > 0 ? (
                            item.therapeuticAreas.slice(0, 3).map((area, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px]">
                                {area}
                              </span>
                            ))
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                  
                  {/* Overall Status */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Risk Signal</span>
                    </td>
                    {items.map((item) => {
                      const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.INSUFFICIENT;
                      const Icon = config.icon;
                      const isWorst = item.status === "PASS";
                      return (
                        <td
                          key={item.query}
                          className={`py-3 px-4 border border-slate-200 text-center ${isWorst ? "bg-red-50" : ""}`}
                        >
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border ${config.color}`}>
                            <Icon size={12} />
                            <span className="font-mono text-xs font-medium">{config.label}</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
              
              {/* M&A Quick Assessment */}
              <div className="mt-6 p-4 bg-slate-50 rounded border border-slate-200">
                <h4 className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-3">Quick M&A Assessment</h4>
                <div className="grid grid-cols-3 gap-4">
                  {items.map((item) => {
                    const score = (item.phase3Count * 3) + (item.phase2Count * 2) + (item.phase1Count) + (item.runwayMonths > 24 ? 2 : 0);
                    const assessment = score >= 10 ? "Strong" : score >= 5 ? "Moderate" : "Weak";
                    const assessmentColor = assessment === "Strong" ? "text-emerald-600" : assessment === "Moderate" ? "text-amber-600" : "text-slate-500";
                    return (
                      <div key={item.query} className="text-center">
                        <div className="font-mono text-sm font-semibold text-slate-900">{item.query}</div>
                        <div className={`text-lg font-bold ${assessmentColor}`}>{assessment}</div>
                        <div className="text-[10px] text-slate-500">Pipeline Score: {score}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left py-3 px-4 bg-slate-50 border border-slate-200 font-mono text-[10px] text-slate-500 uppercase tracking-widest w-36">
                      Metric
                    </th>
                    {items.map((item) => (
                      <th
                        key={item.query}
                        className="text-center py-3 px-4 bg-slate-50 border border-slate-200 min-w-[180px]"
                      >
                        {/* Drug name with ClinicalTrials.gov link */}
                        {!item.isCompany ? (
                          <a
                            href={`https://clinicaltrials.gov/search?intr=${encodeURIComponent(item.query)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center gap-1.5 hover:text-emerald-600 transition-colors"
                            title={`View ${item.query} trials on ClinicalTrials.gov`}
                            data-testid={`ctgov-link-${item.query.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <span className="font-mono text-sm font-semibold text-slate-900 group-hover:text-emerald-600">
                              {item.query.toUpperCase()}
                            </span>
                            <ExternalLink size={11} className="text-slate-400 group-hover:text-emerald-500" />
                          </a>
                        ) : (
                          <span className="font-mono text-sm font-semibold text-slate-900">
                            {item.query.toUpperCase()}
                          </span>
                        )}
                        
                        {/* Phase 93: Smart Alias indicator */}
                        {item.isSimplified && (
                          <div className="mt-1.5 space-y-1">
                            <div className="flex items-center justify-center gap-1">
                              <Sparkles size={10} className="text-emerald-500" />
                              <span className="text-[9px] text-emerald-600 font-medium">Smart Search</span>
                            </div>
                            <div className="text-[9px] text-slate-500 max-w-[160px] truncate mx-auto" title={item.original}>
                              Full: {item.original}
                            </div>
                            <button
                              onClick={() => revertToOriginal(item.original)}
                              className="inline-flex items-center gap-1 text-[9px] text-slate-500 hover:text-slate-700 hover:underline"
                              data-testid={`revert-alias-${item.query.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <RotateCcw size={8} />
                              Use full name
                            </button>
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Full dossier deep-link */}
                  <tr>
                    <td className="py-2 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Full Dossier</span>
                    </td>
                    {items.map((item) => (
                      <td key={item.query} className="py-2 px-4 border border-slate-200 text-center">
                        <button
                          onClick={() => goToDossier(item)}
                          data-testid={`compare-dossier-${item.query.toLowerCase().replace(/\s+/g, '-')}`}
                          className="text-[11px] text-[#2A5D9E] hover:underline inline-flex items-center gap-1"
                        >
                          Open dossier <ExternalLink size={11} />
                        </button>
                      </td>
                    ))}
                  </tr>
                  {/* Status Row */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Status</span>
                    </td>
                    {items.map((item) => {
                      const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.INSUFFICIENT;
                      const Icon = config.icon;
                      const isWorst = item.status === "PASS";
                      return (
                        <td
                          key={item.query}
                          className={`py-3 px-4 border border-slate-200 text-center ${
                            isWorst ? "bg-red-50" : ""
                          }`}
                        >
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border ${config.color}`}>
                            <Icon size={12} />
                            <span className="font-mono text-xs font-medium">{config.label}</span>
                          </div>
                          {item.isTerminated && (
                            <div className="mt-1.5 text-[10px] text-red-600">
                              {item.terminatedCount} terminated trial(s)
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Clinical Phase Row */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Clinical Phase</span>
                    </td>
                    {items.map((item) => {
                      const highlight = phaseHighlights[item.query];
                      return (
                        <td
                          key={item.query}
                          className={`py-3 px-4 border border-slate-200 text-center ${
                            highlight === "best" ? "bg-emerald-50" : ""
                          }`}
                        >
                          <span className={`font-mono text-sm font-semibold ${
                            highlight === "best" ? "text-emerald-700" : "text-slate-900"
                          }`}>
                            {item.highestPhase}
                          </span>
                          {highlight === "best" && (
                            <div className="text-[10px] text-emerald-600 mt-0.5">Most Advanced</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Safety Profile Row */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Safety Profile</span>
                    </td>
                    {items.map((item) => {
                      const highlight = safetyHighlights[item.query];
                      return (
                        <td
                          key={item.query}
                          className={`py-3 px-4 border border-slate-200 text-center ${
                            highlight === "best" ? "bg-emerald-50" : highlight === "worst" ? "bg-red-50" : ""
                          }`}
                        >
                          <span className={`text-xs ${
                            highlight === "best" ? "text-emerald-700 font-semibold" :
                            highlight === "worst" ? "text-red-700 font-semibold" :
                            "text-slate-700"
                          }`}>
                            {item.safetyProfile}
                          </span>
                          {highlight === "best" && item.safetyProfile === "Clean" && (
                            <div className="text-[10px] text-emerald-600 mt-0.5">Cleanest</div>
                          )}
                          {highlight === "worst" && (
                            <div className="text-[10px] text-red-600 mt-0.5">Critical Signals</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Patent Expiry Row */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Patent Expiry</span>
                    </td>
                    {items.map((item) => (
                      <td
                        key={item.query}
                        className="py-3 px-4 border border-slate-200 text-center"
                      >
                        <span className="font-mono text-sm text-slate-700">
                          {item.patentExpiry}
                        </span>
                      </td>
                    ))}
                  </tr>

                  {/* Sponsor Row */}
                  <tr>
                    <td className="py-3 px-4 border border-slate-200 bg-slate-50/50">
                      <span className="label-dense">Lead Sponsor</span>
                    </td>
                    {items.map((item) => (
                      <td
                        key={item.query}
                        className="py-3 px-4 border border-slate-200 text-center"
                      >
                        <span className="text-xs text-slate-700 truncate block max-w-[160px] mx-auto">
                          {item.sponsor}
                        </span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="mt-6 pt-4 border-t border-slate-200">
            <div className="flex items-center gap-6 text-[10px] text-slate-500">
              <span className="font-mono uppercase tracking-widest">Legend:</span>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 bg-emerald-100 border border-emerald-200 rounded-sm" />
                <span>Best in Category</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 bg-red-100 border border-red-200 rounded-sm" />
                <span>Critical / Worst</span>
              </div>
            </div>
            
            {/* Phase 94: Quick links to ClinicalTrials.gov for drugs */}
            {!isCompanyComparison && items.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">
                    View trials:
                  </span>
                  {items.map((item, idx) => (
                    <a
                      key={item.query}
                      href={`https://clinicaltrials.gov/search?intr=${encodeURIComponent(item.query)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 rounded-sm text-[10px] text-slate-600 hover:text-emerald-700 transition-colors"
                      title={`View all ${item.query} trials`}
                    >
                      <FlaskConical size={10} />
                      <span className="font-medium">{item.query}</span>
                      <ExternalLink size={9} className="opacity-50" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
